import React, { useCallback, useEffect, useState } from 'react';
import {
  TextField,
  MenuItem,
  Box,
  InputAdornment,
} from '@mui/material';
import { Phone as PhoneIcon } from '@mui/icons-material';

interface CountryCode {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
}

const countryCodes: CountryCode[] = [
  { code: 'TR', name: 'Turkey', flag: '🇹🇷', dialCode: '+90' },
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49' },
  { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '+39' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '+34' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', dialCode: '+31' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪', dialCode: '+32' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭', dialCode: '+41' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹', dialCode: '+43' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪', dialCode: '+46' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴', dialCode: '+47' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰', dialCode: '+45' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮', dialCode: '+358' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱', dialCode: '+48' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷', dialCode: '+30' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹', dialCode: '+351' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺', dialCode: '+7' },
  { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', dialCode: '+82' },
  { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', dialCode: '+64' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', dialCode: '+52' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '+55' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '+54' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪', dialCode: '+971' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', dialCode: '+20' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dialCode: '+27' },
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  defaultCountryCode?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  label = 'Phone Number',
  required = false,
  error = false,
  helperText,
  disabled = false,
  fullWidth = true,
  defaultCountryCode = '+90',
}) => {
  const parsePhoneNumber = useCallback((phone: string): { countryCode: string; number: string } => {
    if (!phone) {
      return { countryCode: defaultCountryCode, number: '' };
    }

    const matchedCountry = countryCodes.find(c => phone.startsWith(c.dialCode));
    if (matchedCountry) {
      return {
        countryCode: matchedCountry.dialCode,
        number: phone.substring(matchedCountry.dialCode.length).replace(/^[\s-]+/, '').trim(),
      };
    }

    return { countryCode: defaultCountryCode, number: phone };
  }, [defaultCountryCode]);

  const { countryCode: initialCountryCode, number: initialNumber } = parsePhoneNumber(value);
  const [selectedCountryCode, setSelectedCountryCode] = useState(initialCountryCode);
  const [phoneNumber, setPhoneNumber] = useState(initialNumber);

  useEffect(() => {
    const parsed = parsePhoneNumber(value);
    setSelectedCountryCode(parsed.countryCode);
    setPhoneNumber(parsed.number);
  }, [parsePhoneNumber, value]);

  const normalizeLocalNumber = (number: string) => number.replace(/^0+/, '');

  const handleCountryCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCountryCode = event.target.value;
    setSelectedCountryCode(newCountryCode);
    const fullNumber = phoneNumber ? `${newCountryCode}${normalizeLocalNumber(phoneNumber)}` : '';
    onChange(fullNumber);
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = event.target.value.replace(/[^\d\s-]/g, '');
    setPhoneNumber(newNumber);
    const fullNumber = newNumber ? `${selectedCountryCode}${normalizeLocalNumber(newNumber)}` : '';
    onChange(fullNumber);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        width: fullWidth ? '100%' : 'auto',
        alignItems: 'flex-start',
      }}
    >
      <TextField
        select
        value={selectedCountryCode}
        onChange={handleCountryCodeChange}
        disabled={disabled}
        label=" "
        InputLabelProps={{ shrink: true }}
        sx={{
          flex: '0 0 108px',
          width: 108,
          minWidth: 108,
          '& .MuiInputBase-root': {
            height: 56,
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
            py: 0,
            fontSize: '0.95rem',
            fontWeight: 500,
          },
        }}
        SelectProps={{
          renderValue: (selected) => selected as string,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0 }}>
              <PhoneIcon fontSize="small" sx={{ fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
      >
        {countryCodes.map((country) => (
          <MenuItem key={country.code} value={country.dialCode}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '1.1rem' }}>{country.flag}</span>
              <span>{country.dialCode}</span>
              <span style={{ color: '#888', fontSize: '0.85rem' }}>{country.name}</span>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        label={label}
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        required={required}
        error={error}
        helperText={helperText}
        disabled={disabled}
        placeholder="5XX XXX XX XX"
        inputProps={{
          inputMode: 'tel',
        }}
        sx={{ flex: 1, minWidth: 0 }}
      />
    </Box>
  );
};

export default PhoneInput;
